import React, {ReactNode} from 'react'
import styled from '@emotion/styled'

import {Button} from '@vio/atlas/Button'
import {Divider} from '@vio/atlas/Divider'
import {Grid} from '@vio/atlas/helpers/Grid'
import {Icon} from '@vio/atlas/Icon'
import {Popover, PopoverPlacement} from '@vio/atlas/Popover'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {Tooltip, TooltipPlacement} from '@vio/atlas/Tooltip'
import {scrollTo} from '@vio/core/src/_web/utils/browser'
import {FormattedMessageWrapper} from '@vio/core/src/localization/components/FormattedMessage'
import {MAX_NUMBER_OF_ROOMS} from '@vio/core/src/room/business/roomConfiguration/config'
import {
  RoomConfigType,
  RoomSplitType
} from '@vio/core/src/room/types/RoomConfiguration'

import {GuestPicker, GuestPickerButtonMessage} from '../../GuestPicker'
import {useGuestRoomConfig} from '../hooks/useGuestRoomConfig'
import {InfoMessage} from '../InfoMessage'
import {OptimizedRoomGuestPicker} from '../OptimizedRoomGuestPicker'
import {RoomConfigSelector} from '../RoomConfigSelector'

const POPOVER_WIDTH = 400

export type Props = {
  roomConfigType?: RoomConfigType
  isOpen: boolean
  roomsSplit: RoomSplitType[]
  submitButtonMessage?: ReactNode
  onSubmit: (roomsSplit: RoomSplitType[]) => void
  onClose: () => void
  onRoomsChange: (roomsSplit: RoomSplitType[]) => void
  onOptimizeRoomsChange: (isOptimizeRooms: boolean) => void
  onInfoOpen?: () => void
  anchorRef: React.RefObject<HTMLElement>
  verticalOffset?: number
  horizontalOffset?: number
  placement?: PopoverPlacement
  onGuestConfigOptionClick?: (optionSelected: RoomConfigType) => void
}

const PopoverContent = styled.div`
  width: ${POPOVER_WIDTH}px;
`

const RoomConfigurationGrid = styled(Grid)`
  padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s500};
`

const GuestPickerWrapper = styled.div`
  position: relative;
  padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s500};
  max-height: 50vh;
  overflow-y: auto;
  overflow-x: hidden;
`

const ButtonsWrapper = styled.div`
  padding: ${cssTheme.layout.spacing.s400} ${cssTheme.layout.spacing.s500};
  box-sizing: border-box;
`

const AddButtonsWrapper = styled.div`
  margin-bottom: ${cssTheme.layout.spacing.s500};
`

const ConfigureRoomsTitleWrapper = styled.div`
  z-index: 1;
`

const StyledTooltip = styled(Tooltip)`
  margin: 0;
  width: 98%;
`

const ConfigureRoomsTitle = ({onInfoOpen}: {onInfoOpen?: () => void}) => (
  <ConfigureRoomsTitleWrapper>
    <Grid container spacing="s000" gap="s200" alignItems="center">
      <Text variant="titleM">
        <FormattedMessageWrapper
          dataId="configureRooms"
          id="configureRooms"
          defaultMessage="Configure rooms"
        />
      </Text>
      <StyledTooltip
        placement={TooltipPlacement.BottomStart}
        content={<InfoMessage />}
        onRender={onInfoOpen}
      >
        <Icon name="Information" />
      </StyledTooltip>
    </Grid>
  </ConfigureRoomsTitleWrapper>
)

export const GuestRoomConfigPopover = ({
  anchorRef,
  isOpen,
  roomsSplit,
  submitButtonMessage,
  onSubmit,
  onClose,
  onRoomsChange,
  onOptimizeRoomsChange,
  onInfoOpen,
  onGuestConfigOptionClick,
  roomConfigType = RoomConfigType.Optimized,
  verticalOffset,
  horizontalOffset,
  placement = PopoverPlacement.Right
}: Props) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const {
    roomConfigIsOptimized,
    numberOfAdults,
    numberOfChildren,
    numberOfRooms,
    localRoomsSplit,
    handleSubmit,
    handleAddRoom,
    updateRoomsSplit
  } = useGuestRoomConfig({
    roomConfigType,
    roomsSplit,
    onSubmit,
    onRoomsChange
  })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scrollToBottom = () => {
    if (!containerRef.current) return
    scrollTo(containerRef.current, containerRef.current.scrollHeight, 1000)
  }

  const handleAddNewRoom = React.useCallback(() => {
    handleAddRoom()
    scrollToBottom()
  }, [handleAddRoom, scrollToBottom])

  return (
    <Popover
      isOpen={isOpen}
      placement={placement}
      anchorRef={anchorRef}
      verticalOffset={verticalOffset}
      horizontalOffset={horizontalOffset}
      onOutsideClick={onClose}
      className="--guest-room-config-popover"
    >
      <PopoverContent>
        <RoomConfigurationGrid
          container
          spacing="s000"
          gap="s300"
          direction="column"
        >
          <ConfigureRoomsTitle onInfoOpen={onInfoOpen} />
          <RoomConfigSelector
            isOptimized={roomConfigIsOptimized}
            onSelectionClick={onGuestConfigOptionClick}
            onChange={onOptimizeRoomsChange}
          />
        </RoomConfigurationGrid>
        <Divider sideMargin="s500" />
        <GuestPickerWrapper ref={containerRef}>
          {roomConfigIsOptimized ? (
            <OptimizedRoomGuestPicker
              isCompactLayout
              customTitle={
                <FormattedMessageWrapper
                  dataId="addGuests"
                  id="addGuests"
                  defaultMessage="Add guests"
                />
              }
              roomsSplit={localRoomsSplit}
              updateRoomsSplit={updateRoomsSplit}
            />
          ) : (
            <GuestPicker
              isCompactLayout
              roomsSplit={localRoomsSplit}
              updateRoomsSplit={updateRoomsSplit}
            />
          )}
        </GuestPickerWrapper>
      </PopoverContent>
      <ButtonsWrapper>
        {!roomConfigIsOptimized && roomsSplit.length < MAX_NUMBER_OF_ROOMS && (
          <AddButtonsWrapper>
            <Button
              dataId="AddRoomButton"
              variant="tertiary"
              onClick={handleAddNewRoom}
              size="lg"
              fullWidth
              iconStart={<Icon name="Plus" />}
            >
              <FormattedMessageWrapper id="addRoom" defaultMessage="Add room" />
            </Button>
          </AddButtonsWrapper>
        )}
        <Button
          variant="primary"
          dataId="UpdateRoomsGuestsButton"
          onClick={() => handleSubmit(localRoomsSplit)}
          size="xl"
          fullWidth
        >
          {submitButtonMessage}
          <> · </>
          <GuestPickerButtonMessage
            numberOfGuests={numberOfAdults + numberOfChildren}
            numberOfRooms={roomConfigIsOptimized ? undefined : numberOfRooms}
          />
        </Button>
      </ButtonsWrapper>
    </Popover>
  )
}
